import { Bookmark, BookmarkCheck } from 'lucide-react';
import { useUserAuth } from '../contexts/UserAuthContext';
import { track } from '../lib/analytics';

/**
 * SaveButton — bookmark toggle for stories / athletes / videos.
 *
 * iter175 · Fully independent from FollowButton per user request.
 * Tapping "Follow" here writes ONLY to `saved_athletes[]`. Tapping
 * "Get Updates" writes ONLY to `following[]`. Both buckets are merged
 * on the Profile → Following tab (see UserAuthContext.fetchSaved).
 */
const SaveButton = ({ type, id, variant = 'icon', className = '', label = 'Save', savedLabel = 'Saved', theme = 'dark' }) => {
  const { isSaved, toggleSave } = useUserAuth();
  const saved = isSaved(type, id);

  const onClick = (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    track(saved ? `${type.replace(/s$/, '')}_unsaved` : `${type.replace(/s$/, '')}_saved`, {
      [`${type.replace(/s$/, '')}_id`]: id,
    });
    toggleSave(type, id);
  };

  if (variant === 'pill') {
    const savedStyles = theme === 'light' ? 'bg-black text-white' : 'bg-white text-black';
    const unsavedStyles = theme === 'light'
      ? 'bg-black/5 text-black hover:bg-black/10'
      : 'bg-white/10 text-white hover:bg-white/20';
    return (
      <button
        type="button"
        onClick={onClick}
        aria-pressed={saved}
        className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold transition active:scale-95 ${
          saved ? savedStyles : unsavedStyles
        } ${className}`}
        data-testid={`save-${type}-${id}`}
      >
        {saved ? <BookmarkCheck className="w-3.5 h-3.5" /> : <Bookmark className="w-3.5 h-3.5" />}
        {saved ? savedLabel : label}
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={saved}
      aria-label={saved ? 'Remove from saved' : 'Save'}
      className={`inline-flex items-center justify-center w-9 h-9 rounded-full transition active:scale-90 ${
        saved
          ? 'bg-white text-black shadow-md'
          : 'bg-black/60 text-white hover:bg-black/80 backdrop-blur'
      } ${className}`}
      data-testid={`save-${type}-${id}`}
    >
      {saved ? <BookmarkCheck className="w-4 h-4" /> : <Bookmark className="w-4 h-4" />}
    </button>
  );
};

export default SaveButton;
